// Config panel for the "function_block" action block
// Full JavaScript execution on flow variables — like Node-RED's function node
import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  OpenInFull as ExpandIcon,
  CloseFullscreen as CollapseIcon
} from '@mui/icons-material';
import CodeEditor from '../../../components/CodeEditor';

const EXAMPLES = [
  {
    title: 'Math',
    code: 'price * quantity * 1.21',
  },
  {
    title: 'Find in array',
    code: `const items = apiResponse.data || [];
const match = items.find(d => d.name === "AlarmCountdownTime");
match ? match.value : null`,
  },
  {
    title: 'Loop & aggregate',
    code: `const items = sensorData || [];
let sum = 0;
for (const item of items) {
  sum += item.value || 0;
}
output.total = sum;
output.average = items.length ? sum / items.length : 0;
sum`,
  },
  {
    title: 'String manipulation',
    code: `const parts = deviceName.split('-');
output.prefix = parts[0];
output.id = parseInt(parts[1], 10);
parts.join('_').toUpperCase()`,
  },
  {
    title: 'Conditional logic',
    code: `if (temperature > 30) {
  output.status = 'hot';
  output.action = 'cool_down';
} else if (temperature < 10) {
  output.status = 'cold';
  output.action = 'heat_up';
} else {
  output.status = 'normal';
  output.action = 'none';
}
output.status`,
  },
];

const FunctionBlockConfig = ({ config, onChange, onSummaryChange }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCode, setDialogCode] = useState('');

  const handleChange = (field, value) => {
    const updated = { ...config, [field]: value };
    onChange(updated);
    if (field === 'code') {
      const lines = (value || '').split('\n').filter(l => l.trim()).length;
      onSummaryChange(`${lines} line${lines !== 1 ? 's' : ''} of JS`);
    }
  };

  const openDialog = () => {
    setDialogCode(config.code || '');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const applyDialog = () => {
    handleChange('code', dialogCode);
    setDialogOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Write JavaScript code that runs with access to all flow variables.
        The last expression's value is stored in the output variable.
      </Typography>

      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption" component="div">
          <strong>Available in your code:</strong><br />
          &bull; All flow variables directly: <code>locationId</code>, <code>apiResponse</code>, etc.<br />
          &bull; <code>vars.name</code> — same variables via object<br />
          &bull; <code>output.name = value</code> — set multiple output variables<br />
          &bull; <code>console.log()</code> — logged to execution output<br />
          &bull; <code>Math</code>, <code>JSON</code>, <code>Date</code>, <code>RegExp</code>, <code>Array</code>, <code>parseInt</code>, etc.
        </Typography>
      </Alert>

      {/* Code editor */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            JavaScript Code *
          </Typography>
          <Tooltip title="Expand editor">
            <IconButton size="small" onClick={openDialog} sx={{ p: 0.25 }}>
              <ExpandIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <CodeEditor
          value={config.code || ''}
          onChange={(val) => handleChange('code', val)}
          height="250px"
          placeholder="// All variables are available directly&#10;const item = apiResponse.data.find(d => d.name === 'AlarmCountdownTime');&#10;item ? item.value : null"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Last expression = return value. Use output.name for multiple results. 5s timeout.
        </Typography>
      </Box>

      {/* Expanded editor dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '80vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            JavaScript Code Editor
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {(dialogCode || '').split('\n').filter(l => l.trim()).length} lines
            </Typography>
            <IconButton size="small" onClick={closeDialog}>
              <CollapseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Available:</strong> All flow variables directly &bull; <code>vars.name</code> &bull; <code>output.name = value</code> &bull; <code>console.log()</code> &bull; Math, JSON, Date, Array, parseInt, etc.
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <CodeEditor
              value={dialogCode}
              onChange={setDialogCode}
              height="100%"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={closeDialog} size="small" color="inherit">
            Cancel
          </Button>
          <Button onClick={applyDialog} size="small" variant="contained">
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Output variable */}
      <TextField
        size="small"
        label="Store Result As Variable"
        value={config.outputVariable || ''}
        onChange={(e) => handleChange('outputVariable', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
        placeholder="e.g. result"
        helperText={
          config.outputVariable
            ? `Last expression stored as {{${config.outputVariable}}}. Also use output.x for extra vars.`
            : 'Optional. Variable name for the last expression\'s value.'
        }
      />

      {/* Examples */}
      <Accordion variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
            Examples
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {EXAMPLES.map((ex) => (
            <Box
              key={ex.title}
              onClick={() => {
                handleChange('code', ex.code);
              }}
              sx={{
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {ex.title}
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 0.5,
                  fontSize: '0.7rem',
                  fontFamily: '"Fira Code", "Consolas", monospace',
                  color: 'text.secondary',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.4,
                }}
              >
                {ex.code}
              </Box>
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary">
            Click an example to use it as a starting point.
          </Typography>
        </AccordionDetails>
      </Accordion>

      <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
        <Typography variant="caption">
          <strong>Limits:</strong> 5-second timeout. No network access, file system, or require().
          Runs in an isolated sandbox.
        </Typography>
      </Alert>
    </Box>
  );
};

export default FunctionBlockConfig;
